import {
  HashRouter as Router,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import Home from "../components/pages/Login/Login";
import Dashboard from "../components/pages/Dashboard/Dashboard";
import OnBoarding from "../components/pages/Onboard/Onboard";
import SideMenu from "../components/common/SideMenu";
import Header from "../components/Header/Header";

import StaffList from "../components/pages/StaffList/StaffList";
import { StaffDetail } from "../components/pages/StaffDetail/StaffDetail";
import { ComingSoon } from "../components/pages/ComingSoon";
import { DecodedToken } from "../components/common/LoginComponent";
import { jwtDecode } from "jwt-decode";
import {
  readFromLocalStorage,
  writeToLocalStorage,
} from "../utils/DataEncryption";
import { Consent } from "../components/pages/Consent";
import { pageRouteConstants } from "../constants/constants";
import { LocationMaster } from "../components/pages/LocationMaster";
import SsoLogin from "../components/pages/SsoLogin";
import { UserExitFLowComponent } from "../components/common/StaffExitFLowComponent";
import { useEffect } from "react";
import PdfPopup from "../components/common/PdfPopup";

const sideMenuInvisibilityPath = [
  "/",
  pageRouteConstants.onboardRoute,
  pageRouteConstants.tcppRoute,
  pageRouteConstants.locationMaster,
  pageRouteConstants.ssoLogin,
];
const publicPath = [
  "/",
  pageRouteConstants.tcppRoute,
  pageRouteConstants.ssoLogin,
];

export const checkAuthentication = (pathName: string): boolean => {
  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get("token") || readFromLocalStorage("token");
  const appId = urlParams.get("app_id");
  const callBackUrl = urlParams.get("callback_uri");
  const scp = urlParams.get("scp");
  const hash = window.location.hash;
  if (appId || callBackUrl || scp) {
    if(appId){
    sessionStorage.removeItem("token");
    writeToLocalStorage("appId", appId);
    }
    if (callBackUrl) {
      sessionStorage.removeItem("callBackUrl");
      writeToLocalStorage("callBackUrl", callBackUrl);
    }
    if (scp) {
      sessionStorage.removeItem("scp");
      writeToLocalStorage("scp", scp);
    }
    return false;
  }
  if (isPublicPath(pathName)) {
    return true;
  }
 
  if (token && token.trim() !== "") {
    const decodedToken: DecodedToken = jwtDecode(token);
    if (decodedToken && decodedToken.exp) {
      const expiryTime = decodedToken.exp * 1000;
      if (expiryTime > Date.now()) {
        writeToLocalStorage("token", token);
        writeToLocalStorage("userName", decodedToken.firstName);
        writeToLocalStorage("userType", decodedToken.userType);
        writeToLocalStorage("dealerId", decodedToken.dealerId);
        writeToLocalStorage("userId", decodedToken.userId);
        writeToLocalStorage("appId", decodedToken.azp || decodedToken.aud);
        writeToLocalStorage("country", decodedToken.country);
        const application = decodedToken.applications.map(app => {
          const parsed = JSON.parse(app);
          return parsed.applicationName;
        });
        writeToLocalStorage("applications", JSON.stringify(application));
        const decodedBaseRoles = decodedToken.baseRoles;
        const baseRolesArray = decodedBaseRoles.map((roleStr) =>
          JSON.parse(roleStr)
        );
        writeToLocalStorage("baseRoles", JSON.stringify(baseRolesArray));
        writeToLocalStorage("dealershipName", decodedToken.dealerName);

        const dealershipStr = decodedToken?.dealerships?.[0];
        if (dealershipStr) {
          const dealershipObj = JSON.parse(dealershipStr);
          const decodedBranches = dealershipObj?.branches ?? [];
          writeToLocalStorage("branches", JSON.stringify(decodedBranches));
        }
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  } else {
    return false;
  }
};

const isPublicPath = (pathName: string) =>
  publicPath.includes(pathName) ||
  pathName.startsWith(pageRouteConstants.tcpp) ||
  pathName.includes(pageRouteConstants.ssoLogin);

const isSideMenuVisible = (pathName: string) =>
  sideMenuInvisibilityPath.includes(pathName) ||
  pathName.startsWith(pageRouteConstants.tcpp) ||
  pathName.startsWith(pageRouteConstants.ssoLogin) ||
  pathName.includes(pageRouteConstants.onboardRoute);

const allowedRoles = ["Branch Manager", "HR Manager", "Dealer Owner/Partner","General Manager - Sales","General Manager - Service"];

export const isUserAuthorized = (pathName: string) => {
  const token = readFromLocalStorage("token");
  if (isPublicPath(pathName)) {
    return true;
  }
  const baseRoles: any = [];
  const decodedToken: DecodedToken = jwtDecode(token!);
  decodedToken.baseRoles.forEach((baseRole: any) => {
    let roleInfo = JSON.parse(baseRole);
    baseRoles.push(roleInfo.role.name);
  });
  const userType = decodedToken.userType;
  switch (userType) {
    case "DEALER":
      return true;
    case "DEALER_EMPLOYEE":
      return allowedRoles.some((role) => baseRoles.includes(role));
  }
  return false;
};
const AppRouter = () => {
  return (
    <Router>
      <ScrollToTop />
      <SideMenuVisibility />
    </Router>
  );
};

const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const AppRoutes = () => {
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path={pageRouteConstants.dashBoardRoute} element={<Dashboard />} />
      <Route path={pageRouteConstants.onboardRoute} element={<OnBoarding />} />
      <Route path={pageRouteConstants.staffListRoute} element={<StaffList />} />
      <Route
        path={pageRouteConstants.staffDetailRoute}
        element={<StaffDetail />}
      />
      <Route
        path={pageRouteConstants.comingSoonRoute}
        element={<ComingSoon />}
      />
      <Route path={pageRouteConstants.tcppRoute} element={<Consent />} />
      <Route
        path={pageRouteConstants.locationMaster}
        element={<LocationMaster />}
      />
      <Route path={pageRouteConstants.ssoLogin} element={<SsoLogin />} />
      <Route
        path={pageRouteConstants.userExitFlow}
        element={<UserExitFLowComponent />}
      />
      <Route path={pageRouteConstants.helpRoute} element={<PdfPopup />} />
    </Routes>
  );
};

const SideMenuVisibility = () => {
  const location = useLocation();
  let showSideMenuAndHeader = false;
  let IsAuthroizedUser;
  const hashParams = new URLSearchParams(window.location.hash.split("?")[1]);
  if (checkAuthentication(location.pathname)) {
    console.log("inside if", !isSideMenuVisible(location.pathname));
    showSideMenuAndHeader = !isSideMenuVisible(location.pathname);
    IsAuthroizedUser = isUserAuthorized(location.pathname);
  } else {
    window.location.href = "/";
  }
  return IsAuthroizedUser ? (
    showSideMenuAndHeader ? (
      <MainLayout />
    ) : (
      <MainLayoutWithoutSideBar />
    )
  ) : (
    <UnAuthroizedUserLayout />
  );
};

export const MainLayoutWithoutSideBar = () => {
  return <AppRoutes />;
};

export const UnAuthroizedUserLayout = () => {
  return (
    <div className="grid-container">
      <div className="grid-header">
        <Header />
      </div>

      <div className="flex-container">
        <p className="centered-text">
          You are not authorized to use this application.
        </p>
      </div>
    </div>
  );
};

export const MainLayout = () => {
  return (
    <div className="grid-container">
      <div className=" w-full flex">
        <div>
          <SideMenu />
        </div>
        <div className=" w-screen h-screen main">
          <Header />
          <AppRoutes />
        </div>
      </div>
      <div className="main"></div>
    </div>
  );
};

export default AppRouter;
